import type { QueryClient } from '@tanstack/react-query';
import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import type { AccountConnections, CalendarEvent } from '~/src/types/api';

type CalendarProvider = 'google' | 'microsoft';

const CALENDAR_PROVIDERS: CalendarProvider[] = ['google', 'microsoft'];

type UseCalendarProviderResetParams = {
  accountConnections?: AccountConnections;
  isConnectionsLoading: boolean;
  queryClient: QueryClient;
  googleCacheRef: MutableRefObject<
    Record<string, { events: CalendarEvent[]; nextPageToken?: string; isBackfilled?: boolean }>
  >;
  microsoftCacheRef: MutableRefObject<
    Record<string, { events: CalendarEvent[]; nextLink?: string; isBackfilled?: boolean }>
  >;
  googleSyncTokenRef: MutableRefObject<Record<string, string | undefined>>;
  microsoftDeltaLinkRef: MutableRefObject<Record<string, string | undefined>>;
  setGoogleEvents: Dispatch<SetStateAction<CalendarEvent[]>>;
  setGoogleNextPageToken: Dispatch<SetStateAction<string | undefined>>;
  setMicrosoftEvents: Dispatch<SetStateAction<CalendarEvent[]>>;
  setMicrosoftNextLink: Dispatch<SetStateAction<string | undefined>>;
};

/**
 * Drops every trace of a provider's events once it is no longer connected.
 *
 * The panel keeps one event array per provider and renders both concatenated,
 * so events left behind by a disconnected account would otherwise keep showing
 * up next to the newly connected account's events until a full page reload.
 * Clearing the per-tab caches also matters on reconnect: the first-page queries
 * stay disabled while a cache entry exists for the active tab.
 */
export const useCalendarProviderReset = ({
  accountConnections,
  isConnectionsLoading,
  queryClient,
  googleCacheRef,
  microsoftCacheRef,
  googleSyncTokenRef,
  microsoftDeltaLinkRef,
  setGoogleEvents,
  setGoogleNextPageToken,
  setMicrosoftEvents,
  setMicrosoftNextLink,
}: UseCalendarProviderResetParams) => {
  const resetProviderEvents = useCallback(
    (provider: CalendarProvider) => {
      if (provider === 'google') {
        setGoogleEvents([]);
        setGoogleNextPageToken(undefined);
        googleCacheRef.current = {};
        googleSyncTokenRef.current = {};
        void queryClient.removeQueries({ queryKey: ['googleEvents'] });
        return;
      }

      setMicrosoftEvents([]);
      setMicrosoftNextLink(undefined);
      microsoftCacheRef.current = {};
      microsoftDeltaLinkRef.current = {};
      void queryClient.removeQueries({ queryKey: ['microsoftEvents'] });
    },
    [
      googleCacheRef,
      googleSyncTokenRef,
      microsoftCacheRef,
      microsoftDeltaLinkRef,
      queryClient,
      setGoogleEvents,
      setGoogleNextPageToken,
      setMicrosoftEvents,
      setMicrosoftNextLink,
    ],
  );

  const wasConnectedRef = useRef<Record<CalendarProvider, boolean>>({
    google: false,
    microsoft: false,
  });

  // A provider can be dropped from the Settings modal, the TeamHub settings
  // plugin, another tab, or a revoked grant, so key off the connection state
  // itself rather than off any single disconnect path.
  useEffect(() => {
    if (isConnectionsLoading || !accountConnections) {
      return;
    }

    for (const provider of CALENDAR_PROVIDERS) {
      const isConnected = !!accountConnections[provider]?.connected;
      if (wasConnectedRef.current[provider] && !isConnected) {
        resetProviderEvents(provider);
      }
      wasConnectedRef.current[provider] = isConnected;
    }
  }, [accountConnections, isConnectionsLoading, resetProviderEvents]);

  return { resetProviderEvents };
};
